import { Directive, DestroyRef, HostListener, inject, Input, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, of, switchMap, timer, takeWhile, take, takeUntil, concatMap } from 'rxjs';
import {
  InventoryService,
  FranchiseResultModel,
} from '../../../../../core/services/inventory/inventory.service';
import { customFranchiseSearchFn } from '../../../../../core/helpers/franchise-search.helper';
import { getHttpErrorMessage } from '../../../../../core/helpers/http-error-response.helper';
import {
  InventoryHistoryItem,
  InventoryHistorySearchRequest,
  WAREHOUSE_OPTIONS,
} from '../../../../../core/models/inventory/inventory-history.model';
import { PaginationState } from '../../../../../core/models/common/pageable.model';
import { createPaginationState } from '../../../../../core/helpers/pagination-state.helper';

const POLLING_INTERVAL = 5000;
const MAX_POLLING_ATTEMPTS = 5;
const PAGE_SIZE = 10;

@Directive()
export abstract class InventoryHistoryBase {
  @Input({ required: true }) skuId!: number;
  @Input({ required: true }) franchises!: FranchiseResultModel[];
  @Input() loadingAccounts = false;

  selectedAccounts: string[] = [];
  selectedWarehouses: string[] = [];
  dateFrom = '';
  dateTo = '';
  timeFrom = '';
  timeTo = '';
  loading = false;
  error: string | null = null;
  allData: InventoryHistoryItem[] = [];
  data: InventoryHistoryItem[] = [];
  hasSearched = false;
  pollingAttempt = 0;
  paginationState: WritableSignal<PaginationState> = createPaginationState({
    itemsPerPage: PAGE_SIZE,
  });

  readonly warehouseOptions = WAREHOUSE_OPTIONS;
  customSearchFn = customFranchiseSearchFn;

  activePreset: string | null = null;
  showDatePicker = false;
  showFilterMenu = false;
  visibleFilters = new Set<string>();
  editingFilter: string | null = null;

  protected readonly destroyRef = inject(DestroyRef);
  protected readonly inventory = inject(InventoryService);
  private readonly cancelPolling$ = new Subject<void>();

  @HostListener('document:click')
  onDocumentClick(): void {
    this.showDatePicker = false;
    this.showFilterMenu = false;
    this.editingFilter = null;
  }

  search(): void {
    this.cancelPolling$.next();
    this.error = null;
    this.loading = true;
    this.hasSearched = false;
    this.allData = [];
    this.data = [];
    this.pollingAttempt = 0;

    const req: InventoryHistorySearchRequest = {
      filters: {
        skuId: this.skuId,
        accounts: this.selectedAccounts,
      },
      sort: [{ field: 'date', direction: 'ASC' }],
      size: 10000,
    };

    if (this.selectedWarehouses.length) {
      req.filters.warehouseNames = this.selectedWarehouses;
    }
    if (this.dateFrom || this.dateTo) {
      req.filters.date = {};
      if (this.dateFrom) req.filters.date.from = this.dateFrom;
      if (this.dateTo) req.filters.date.to = this.dateTo;
    }
    if (this.timeFrom || this.timeTo) {
      req.filters.time = {};
      if (this.timeFrom) req.filters.time.from = this.timeFrom;
      if (this.timeTo) req.filters.time.to = this.timeTo;
    }

    this.inventory
      .searchHistory(req)
      .pipe(
        switchMap(response => {
          if (response.status === 'COMPLETED') {
            return of({
              status: 'COMPLETED' as const,
              content: response.content,
            });
          }
          return timer(0, POLLING_INTERVAL).pipe(
            concatMap(() => {
              this.pollingAttempt++;
              return this.inventory.getHistoryResult(response.jobId!);
            }),
            takeWhile(result => result.status === 'PENDING', true),
            take(MAX_POLLING_ATTEMPTS)
          );
        }),
        takeUntil(this.cancelPolling$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => {
          if (result.status === 'COMPLETED') {
            this.allData = result.content ?? [];
            this.hasSearched = true;
            this.loading = false;
            this.updatePage(0);
          } else if (result.status === 'FAILED') {
            this.loading = false;
            this.error = result.error ?? 'Erro ao processar histórico de estoque.';
          }
        },
        error: err => {
          console.error(err);
          this.loading = false;
          this.error = getHttpErrorMessage(err);
        },
        complete: () => {
          if (this.loading) {
            this.loading = false;
            this.error = 'Tempo de consulta esgotado. Tente novamente.';
          }
        },
      });
  }

  private updatePage(page: number): void {
    const start = page * PAGE_SIZE;
    this.data = this.allData.slice(start, start + PAGE_SIZE);
    this.paginationState.set({
      currentPage: page,
      totalItems: this.allData.length,
      currentPageItems: this.data.length,
      itemsPerPage: PAGE_SIZE,
      loading: false,
    });
  }

  onPaginationChange = (page: number, _pageSize?: number): void => {
    this.updatePage(page);
  };

  get totalPages(): number {
    return Math.ceil(this.allData.length / PAGE_SIZE) || 1;
  }

  get emptyRows(): number[] {
    const remaining = PAGE_SIZE - this.data.length;
    return remaining > 0 ? Array(remaining).fill(0) : [];
  }

  setDatePreset(preset: 'today' | '7d' | '30d' | 'month'): void {
    if (this.activePreset === preset) {
      this.activePreset = null;
      this.dateFrom = '';
      this.dateTo = '';
      return;
    }

    const today = new Date();
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    this.dateTo = fmt(today);

    switch (preset) {
      case 'today':
        this.dateFrom = fmt(today);
        break;
      case '7d': {
        const d = new Date(today);
        d.setDate(d.getDate() - 7);
        this.dateFrom = fmt(d);
        break;
      }
      case '30d': {
        const d = new Date(today);
        d.setDate(d.getDate() - 30);
        this.dateFrom = fmt(d);
        break;
      }
      case 'month':
        this.dateFrom = fmt(new Date(today.getFullYear(), today.getMonth(), 1));
        break;
    }
    this.activePreset = preset;
  }

  getPresetLabel(preset: string): string {
    const labels: Record<string, string> = {
      today: 'Hoje',
      '7d': 'Últimos 7 dias',
      '30d': 'Últimos 30 dias',
      month: 'Este mês',
    };
    return labels[preset] ?? preset;
  }

  get activeFilters(): { type: string; label: string; values: string[] }[] {
    const filters: { type: string; label: string; values: string[] }[] = [];
    if (this.selectedAccounts.length) {
      filters.push({
        type: 'accounts',
        label: 'Contas',
        values: this.selectedAccounts.map(a => a.toUpperCase()),
      });
    }
    if (this.selectedWarehouses.length) {
      filters.push({
        type: 'warehouses',
        label: 'Ruas',
        values: this.selectedWarehouses.map(w => this.getWarehouseLabel(w)),
      });
    }
    if (this.dateFrom || this.dateTo) {
      const label = this.activePreset
        ? this.getPresetLabel(this.activePreset)
        : `${this.dateFrom || '…'} → ${this.dateTo || '…'}`;
      filters.push({ type: 'period', label: 'Período', values: [label] });
    }
    return filters;
  }

  get activeFilterCount(): number {
    return this.activeFilters.reduce((sum, f) => sum + f.values.length, 0);
  }

  get activeFilterSummary(): string {
    const parts: string[] = [];
    if (this.selectedAccounts.length) parts.push(`${this.selectedAccounts.length} conta(s)`);
    if (this.selectedWarehouses.length) parts.push(`${this.selectedWarehouses.length} rua(s)`);
    if (this.dateFrom || this.dateTo) {
      parts.push(this.activePreset ? this.getPresetLabel(this.activePreset) : 'Período custom');
    }
    return parts.join(' · ');
  }

  getWarehouseLabel(value: string): string {
    const opt = this.warehouseOptions.find(o => o.value === value);
    return opt?.label ?? value;
  }

  removeFilter(type: string, value?: string): void {
    switch (type) {
      case 'accounts':
        if (value) {
          this.selectedAccounts = this.selectedAccounts.filter(a => a.toUpperCase() !== value);
        } else {
          this.selectedAccounts = [];
        }
        break;
      case 'warehouses':
        if (value) {
          const opt = this.warehouseOptions.find(o => o.label === value);
          this.selectedWarehouses = this.selectedWarehouses.filter(w => w !== (opt?.value ?? value));
        } else {
          this.selectedWarehouses = [];
        }
        break;
      case 'period':
        this.dateFrom = '';
        this.dateTo = '';
        this.timeFrom = '';
        this.timeTo = '';
        this.activePreset = null;
        break;
    }
  }

  clearAllFilters(): void {
    this.selectedAccounts = [];
    this.selectedWarehouses = [];
    this.dateFrom = '';
    this.dateTo = '';
    this.timeFrom = '';
    this.timeTo = '';
    this.activePreset = null;
  }

  onDateFromChange(): void {
    if (this.dateFrom && this.dateTo && this.dateFrom > this.dateTo) {
      this.dateTo = this.dateFrom;
    }
    this.activePreset = null;
  }

  onDateToChange(): void {
    if (this.dateFrom && this.dateTo && this.dateTo < this.dateFrom) {
      this.dateFrom = this.dateTo;
    }
    this.activePreset = null;
  }

  toggleDatePicker(): void {
    this.showDatePicker = !this.showDatePicker;
    this.showFilterMenu = false;
    this.editingFilter = null;
  }

  toggleFilterMenu(): void {
    this.showFilterMenu = !this.showFilterMenu;
    this.showDatePicker = false;
    this.editingFilter = null;
  }

  toggleEditingFilter(type: string): void {
    this.editingFilter = this.editingFilter === type ? null : type;
    this.showFilterMenu = false;
    this.showDatePicker = false;
  }

  addFilter(type: string): void {
    this.visibleFilters.add(type);
    this.showFilterMenu = false;
    this.editingFilter = type;
  }

  isFilterVisible(type: string): boolean {
    return this.visibleFilters.has(type);
  }

  removeVisibleFilter(type: string): void {
    this.visibleFilters.delete(type);
    this.removeFilter(type);
    this.editingFilter = null;
  }
}
